import BaseInput from '@components/v2/form/BaseInput/BaseInput';
import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Loading from '@components/v2/ui/Loading/Loading';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import { Box } from '@mui/material';
import { Camera, CircleAlert } from 'lucide-react';
import { FocusEvent, KeyboardEvent } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { ClarityEvents } from '../../../../../lib/clarity/types/clarityEvents.types';
import { withEvent } from '../../../../../lib/clarity/utils/captureEvent.utils';
import { AddOrderProductTestIds } from '../../../../../utils/testIds/OrderProducts.testIds';
import { useTranslation } from '../../../../translation/TranslationContext';
import BarcodeReaderDialog from '../barcode-reader/BarcodeReaderDialog';
import {
  codeFieldCameraButtonSx,
  codeFieldContainerSx,
  codeFieldErrorIconSx,
  codeFieldStatusSlotSx,
  codeInputSx,
} from './addOrderProductCodeField.styles';
import { AddOrderProductRowProductConfig } from './addOrderProductRow.types';
import useBarcodeReader from './hooks/useBarcodeReader';

const AddOrderProductCodeField = ({
  inputProductValue,
  isLoadingProduct,
  hasError,
  errorMessage,
  codeInputRef,
  onChangeCode,
  onSubmitCode,
  onBlurCode,
}: AddOrderProductRowProductConfig) => {
  const { translate } = useTranslation();
  const barcodeReader = useBarcodeReader({ onDecode: onSubmitCode });
  const isCameraEnabled = !isLoadingProduct && !hasError;

  const openCameraLabel = translate(LangEnum.GLOBAL_OPEN_CAMERA);

  const renderStatus = () => {
    if (isLoadingProduct) {
      return <Loading size={16} />;
    }

    if (hasError) {
      return (
        <Box sx={codeFieldErrorIconSx}>
          <Icon icon={CircleAlert} size="md" />
        </Box>
      );
    }

    return (
      <IconButton
        icon={Camera}
        size="medium"
        ariaLabel={openCameraLabel}
        sx={codeFieldCameraButtonSx}
        onClick={withEvent(ClarityEvents.ORDER_SCREEN_CLICKED_BARCODE_READER, barcodeReader.open)}
      />
    );
  };

  const getStatusTooltip = () => {
    if (hasError) {
      return errorMessage ?? translate(LangEnum.ORDER_PRODUCTS_NOT_FOUND_MESSAGE);
    }

    if (isCameraEnabled) {
      return openCameraLabel;
    }

    return undefined;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSubmitCode((event.target as HTMLInputElement).value);
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onBlurCode(event.target.value);
  };

  return (
    <Box sx={codeFieldContainerSx}>
      <BaseInput
        ref={codeInputRef}
        autoFocus
        error={hasError}
        value={inputProductValue}
        placeholder={translate(LangEnum.GLOBAL_CODE)}
        testId={AddOrderProductTestIds.PRODUCT_CODE_INPUT}
        ariaLabel={translate(LangEnum.ADD_ORDER_PRODUCTS_PRODUCT_CODE_LABEL)}
        onChange={(event) => onChangeCode(event.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        sx={codeInputSx}
      />

      <Box sx={codeFieldStatusSlotSx}>
        <Tooltip TooltipContent={getStatusTooltip()} tabIndex={-1}>
          {renderStatus()}
        </Tooltip>
      </Box>

      <BarcodeReaderDialog
        open={barcodeReader.isOpen}
        onClose={barcodeReader.close}
        onSuccess={barcodeReader.handleSuccess}
      />
    </Box>
  );
};

export default AddOrderProductCodeField;
